import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import type { JobPlanVerticalKey } from '@agentic-flow-studio/shared/types/job-plan';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Input } from '@agentic-flow-studio/shared/ui/input';
import { ScrollArea } from '@agentic-flow-studio/shared/ui/scroll-area';
import { useChat } from '@ai-sdk/react';
import { getToolName, isTextUIPart, isToolUIPart, type UIMessage } from 'ai';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronRight,
  FileText,
  Loader2,
  MapPin,
  Maximize2,
  Minimize2,
  PanelRightClose,
  Paperclip,
  Phone,
  Send,
  SlidersHorizontal,
  Sparkles,
  Square,
  Trash2,
  Upload,
  User,
  X,
  Zap,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { useJobPlanChatTransport } from '../../hooks/useJobPlanChatTransport';

const VERTICAL_EMOJIS: Record<JobPlanVerticalKey, string> = {
  trades: '🔧',
  logistics: '🚚',
  'it-telecom': '💻',
  healthcare: '🩺',
  facilities: '🧹',
  general: '📋',
};

const VERTICAL_NAMES: Record<JobPlanVerticalKey, string> = {
  trades: 'Trades & Maintenance',
  logistics: 'Logistics & Couriers',
  'it-telecom': 'IT & Telecom',
  healthcare: 'Healthcare & Care',
  facilities: 'Facilities & Cleaning',
  general: 'General Field Service',
};

const SUGGESTED_PROMPTS = [
  {
    icon: '📸',
    label: 'Extract from screenshot',
    prompt:
      'Please extract all jobs, addresses, customer details, and required skills from this screenshot.',
  },
  {
    icon: '🚚',
    label: 'Optimize route schedule',
    prompt:
      "Optimize today's schedule with a balanced workload strategy and summarize the proposal.",
  },
  {
    icon: '📍',
    label: 'Check unassigned jobs',
    prompt: 'Check all unassigned jobs in the pool and tell me which operators are available.',
  },
  {
    icon: '⚡',
    label: 'Create urgent job',
    prompt:
      'Create an urgent emergency callout job for AC repair at 100 Queen St for John Smith (0412 345 678), duration 60 mins.',
  },
];

interface JobPlanCopilotPanelProps {
  serviceDate: string;
  isOpen: boolean;
  onToggle: () => void;
  onOpenIngestDrawer?: () => void;
}

export function JobPlanCopilotPanel({
  serviceDate,
  isOpen,
  onToggle,
  onOpenIngestDrawer,
}: JobPlanCopilotPanelProps) {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const utils = trpc.useUtils();

  const [isWide, setIsWide] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [attachedFileBase64, setAttachedFileBase64] = useState<string | null>(null);
  const [attachedFileName, setAttachedFileName] = useState<string | null>(null);
  const [attachedFileMime, setAttachedFileMime] = useState<string>('application/pdf');
  const [attachedFileSize, setAttachedFileSize] = useState<string | null>(null);
  const [input, setInput] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const transport = useJobPlanChatTransport(serviceDate);

  const { data: jobPlanSettings } = trpc.jobPlan.getSettings.useQuery();

  const { messages, sendMessage, status, stop, setMessages } = useChat({
    transport,
    onFinish: () => {
      // Invalidate relevant queries when agent finishes actions
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
      utils.jobPlan.listProposals.invalidate();
      utils.jobPlan.jobs.list.invalidate();
    },
    onError: (err) => {
      toast.error(`Copilot error: ${err.message}`);
    },
  });

  const isLoading = status === 'submitted' || status === 'streaming';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  });

  // Handle PDF / image file selection
  const processAttachedFile = (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      toast.error('Please upload a PDF document (.pdf) or image (.png, .jpg, .webp)');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File must be under 20MB');
      return;
    }

    const sizeFormatted =
      file.size > 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.round(file.size / 1024)} KB`;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setAttachedFileBase64(base64);
      setAttachedFileMime(file.type || (isPdf ? 'application/pdf' : 'image/png'));
      setAttachedFileName(file.name);
      setAttachedFileSize(sizeFormatted);
      toast.info(`Attached ${isPdf ? 'PDF document' : 'image'}: ${file.name}`);
    };
    reader.readAsDataURL(file);
  };

  // Clipboard paste listener (supports screenshot images)
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          processAttachedFile(file);
          e.preventDefault();
          break;
        }
      }
    }
  };

  // Form submit wrapper supporting PDF & image attachments
  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() && !attachedFileBase64) return;

    let finalPrompt = input.trim();
    if (attachedFileBase64 && !finalPrompt) {
      const isPdf =
        attachedFileMime === 'application/pdf' || attachedFileName?.toLowerCase().endsWith('.pdf');
      finalPrompt = isPdf
        ? `Please extract all jobs, customer contacts, site addresses, arrival windows, compliance certifications (e.g. Dwelling CC Certificates), and trade skills from this uploaded PDF work order (${attachedFileName || 'work_order.pdf'}).`
        : 'Please extract all jobs, addresses, customer details, and required skills from this attached screenshot.';
    }

    const filePayload = attachedFileBase64
      ? {
          base64: attachedFileBase64,
          name: attachedFileName || 'document.pdf',
          mimeType: attachedFileMime,
        }
      : undefined;

    sendMessage(
      { text: finalPrompt },
      filePayload
        ? {
            body: {
              attachedFile: filePayload,
            },
          }
        : undefined
    );
    setInput('');
    setAttachedFileBase64(null);
    setAttachedFileName(null);
    setAttachedFileSize(null);
  };

  const verticalKey = (jobPlanSettings?.verticalKey || 'trades') as JobPlanVerticalKey;

  // Confirm proposal mutation from chat card
  const confirmProposalMutation = trpc.jobPlan.confirmProposals.useMutation({
    onSuccess: (res) => {
      const count = res.results.filter((r) => r.success).length;
      toast.success(`Successfully confirmed ${count} job(s) into the schedule!`);
      utils.jobPlan.getDaySchedule.invalidate({ serviceDate });
      utils.jobPlan.listProposals.invalidate();
      utils.jobPlan.jobs.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all font-medium text-sm border border-primary/20"
        title="Open Job Plan Copilot"
      >
        <Sparkles className="h-5 w-5 animate-pulse" />
        <span className="font-semibold">AI Copilot</span>
      </button>
    );
  }

  return (
    <aside
      className={`relative flex flex-col h-full bg-card border-l border-border transition-all duration-200 z-20 shrink-0 ${
        isWide ? 'w-[520px]' : 'w-[400px]'
      }`}
      onPaste={handlePaste}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) processAttachedFile(file);
      }}
    >
      {/* Drag & drop overlay */}
      {dragOver && (
        <div className="absolute inset-0 bg-primary/10 backdrop-blur-xs border-2 border-dashed border-primary z-50 flex flex-col items-center justify-center p-6 text-center animate-fade-in pointer-events-none">
          <Upload className="h-10 w-10 text-primary animate-bounce mb-2" />
          <p className="text-sm font-semibold text-primary">Drop PDF work order or screenshot</p>
          <p className="text-xs text-muted-foreground mt-1">
            Multimodal Gemini will read addresses, CC certificates, contacts & work orders
          </p>
        </div>
      )}

      {/* Copilot Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card/60 backdrop-blur-xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm tracking-tight text-foreground truncate">
                Job Plan Copilot
              </span>
              <span className="text-xs">{VERTICAL_EMOJIS[verticalKey]}</span>
            </div>
            <span className="text-[11px] text-muted-foreground truncate">
              {VERTICAL_NAMES[verticalKey]} • AI Agent
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {orgId && workspaceId && (
            <Link
              to={`/org/${orgId}/workspace/${workspaceId}/settings/job-plan`}
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              title="Job Plan Settings"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Link>
          )}

          <button
            type="button"
            onClick={() => setMessages([])}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            title="Clear Chat History"
          >
            <Trash2 className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={() => setIsWide(!isWide)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors hidden sm:block"
            title={isWide ? 'Compact width' : 'Expand width'}
          >
            {isWide ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>

          <button
            type="button"
            onClick={onToggle}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            title="Close Copilot Panel"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Chat Messages Stream */}
      <ScrollArea className="flex-1 p-4">
        <div className="flex flex-col gap-4">
          {/* Empty state & prompt chips */}
          {messages.length === 0 && (
            <div className="flex flex-col gap-4 py-4 animate-fade-in">
              <div className="p-4 rounded-xl border border-border/80 bg-muted/20 flex flex-col gap-2.5">
                <div className="flex items-center gap-2 text-primary font-medium text-xs">
                  <Bot className="h-4 w-4" />
                  <span>How can I assist with your job plan today?</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  I can extract jobs from screenshots or notes, schedule proposals, analyze route
                  efficiency, or find operators with the right trade certifications.
                </p>
                <div className="pt-1 flex items-center gap-2 text-[11px] text-muted-foreground/80">
                  <span className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">
                    Ctrl+V
                  </span>
                  <span>Paste screenshot anywhere to extract</span>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-1">
                  Quick Actions
                </span>
                <div className="grid grid-cols-1 gap-2">
                  {SUGGESTED_PROMPTS.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => setInput(item.prompt)}
                      className="text-left p-2.5 rounded-lg border border-border/60 hover:border-primary/50 hover:bg-muted/40 transition-all text-xs flex items-center justify-between group bg-card"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm shrink-0">{item.icon}</span>
                        <span className="font-medium text-foreground group-hover:text-primary transition-colors truncate">
                          {item.label}
                        </span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message: UIMessage) => {
            const isUser = message.role === 'user';

            return (
              <div
                key={message.id}
                className={`flex flex-col gap-1.5 ${isUser ? 'items-end' : 'items-start'}`}
              >
                {/* Avatar / Sender */}
                <div className="flex items-center gap-1.5 px-1">
                  {isUser ? (
                    <span className="text-[11px] font-medium text-muted-foreground">You</span>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-primary" />
                      <span className="text-[11px] font-medium text-primary">Copilot</span>
                    </div>
                  )}
                </div>

                {/* Message Bubble */}
                <div
                  className={`rounded-2xl px-4 py-3 text-xs leading-relaxed max-w-[95%] shadow-2xs ${
                    isUser
                      ? 'bg-primary text-primary-foreground rounded-tr-xs'
                      : 'bg-muted/60 text-foreground border border-border/70 rounded-tl-xs'
                  }`}
                >
                  {message.parts?.map((part, index) => {
                    if (isTextUIPart(part)) {
                      return (
                        <div
                          key={index}
                          className="prose prose-xs dark:prose-invert max-w-none break-words"
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.text}</ReactMarkdown>
                        </div>
                      );
                    }

                    if (isToolUIPart(part)) {
                      const toolName = getToolName(part);
                      const isPending =
                        part.state === 'input-streaming' || part.state === 'input-available';
                      const isError = part.state === 'output-error';
                      const isDone = part.state === 'output-available';

                      // Interactive Proposal Extracted Card
                      if (toolName === 'ingest_job_plan_data' && isDone && part.output) {
                        const out = part.output as {
                          extractedCount: number;
                          proposals: Array<{
                            id: number;
                            title: string;
                            formattedAddress: string;
                            contactName?: string;
                            contactPhone?: string;
                            contactEmail?: string;
                            durationMinutes: number;
                            serviceDate: string;
                          }>;
                        };

                        return (
                          <div
                            key={index}
                            className="mt-2.5 p-3 rounded-xl border border-primary/30 bg-primary/5 flex flex-col gap-2 text-foreground"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-xs flex items-center gap-1.5 text-primary">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                <span>{out.extractedCount} Jobs Staged for Review</span>
                              </span>
                              <Badge variant="outline" className="text-[10px]">
                                Ingest Success
                              </Badge>
                            </div>

                            <div className="flex flex-col gap-1.5 max-h-48 overflow-y-auto">
                              {out.proposals?.map((p) => (
                                <div
                                  key={p.id}
                                  className="p-2 rounded-lg bg-background border border-border/60 flex flex-col gap-1 text-[11px]"
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium truncate">{p.title}</span>
                                    <span className="text-[10px] text-muted-foreground">
                                      {p.durationMinutes}m
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-muted-foreground flex items-center gap-1 truncate">
                                    <MapPin className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{p.formattedAddress}</span>
                                  </div>
                                  {(p.contactName || p.contactPhone) && (
                                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                                      {p.contactName && (
                                        <span className="flex items-center gap-1">
                                          <User className="h-2.5 w-2.5" />
                                          <span>{p.contactName}</span>
                                        </span>
                                      )}
                                      {p.contactPhone && (
                                        <span className="flex items-center gap-1">
                                          <Phone className="h-2.5 w-2.5" />
                                          <span>{p.contactPhone}</span>
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {out.proposals?.length > 0 && (
                              <div className="pt-1 flex items-center gap-2">
                                <Button
                                  size="sm"
                                  className="h-7 text-xs flex-1 gap-1.5"
                                  disabled={confirmProposalMutation.isPending}
                                  onClick={() =>
                                    confirmProposalMutation.mutate({
                                      proposals: out.proposals.map((p) => ({ proposalId: p.id })),
                                    })
                                  }
                                >
                                  {confirmProposalMutation.isPending ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                  )}
                                  <span>Confirm & Add to Schedule</span>
                                </Button>
                                {onOpenIngestDrawer && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={onOpenIngestDrawer}
                                  >
                                    Review All
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      }

                      // Interactive Optimization Card
                      if (toolName === 'optimize_schedule' && isDone && part.output) {
                        const out = part.output as {
                          runId: number;
                          strategy: string;
                          totalAssignedStops: number;
                          routesSummary: Array<{
                            operatorName: string;
                            stopCount: number;
                            distanceKm: number;
                            durationHours: string;
                          }>;
                        };

                        return (
                          <div
                            key={index}
                            className="mt-2.5 p-3 rounded-xl border border-amber-500/30 bg-amber-500/5 flex flex-col gap-2 text-foreground"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-xs flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                                <Zap className="h-3.5 w-3.5" />
                                <span>Optimization Proposal Ready</span>
                              </span>
                              <Badge variant="outline" className="text-[10px] capitalize">
                                {out.strategy?.replace('_', ' ')}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-[11px] p-2 bg-background rounded-lg border border-border/60">
                              <div>
                                <span className="text-muted-foreground block text-[10px]">
                                  Stops Assigned
                                </span>
                                <span className="font-semibold">
                                  {out.totalAssignedStops} stops
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground block text-[10px]">
                                  Active Drivers
                                </span>
                                <span className="font-semibold">
                                  {out.routesSummary?.length || 0} operators
                                </span>
                              </div>
                            </div>

                            <div className="flex flex-col gap-1 max-h-36 overflow-y-auto">
                              {out.routesSummary?.map((r, i) => (
                                <div
                                  key={i}
                                  className="text-[10px] flex items-center justify-between px-2 py-1 bg-muted/40 rounded"
                                >
                                  <span className="font-medium">{r.operatorName}</span>
                                  <span className="text-muted-foreground">
                                    {r.stopCount} stops • {r.distanceKm} km ({r.durationHours}h)
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }

                      // Default Tool Status Chip
                      return (
                        <div
                          key={index}
                          className="my-1.5 flex items-center gap-2 py-1 px-2 rounded-md bg-background/80 border border-border/60 text-[11px]"
                        >
                          {isPending && <Loader2 className="h-3 w-3 animate-spin text-primary" />}
                          {isDone && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                          {isError && <AlertTriangle className="h-3 w-3 text-destructive" />}
                          <span className="text-muted-foreground font-mono text-[10px]">
                            {toolName.replace(/_/g, ' ')}
                          </span>
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            );
          })}

          {/* Loading status indicator */}
          {isLoading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-1 px-2 animate-pulse">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span>Copilot is reasoning & executing tools…</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Attachment Preview Strip (PDF & Images) */}
      {attachedFileBase64 && (
        <div className="px-4 py-2.5 border-t border-border bg-muted/40 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-2.5 min-w-0">
            {attachedFileMime === 'application/pdf' ||
            attachedFileName?.toLowerCase().endsWith('.pdf') ? (
              <div className="h-10 w-10 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                <FileText className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              </div>
            ) : (
              <img
                src={attachedFileBase64}
                alt="Attachment preview"
                className="h-10 w-10 object-cover rounded-lg border border-border shrink-0"
              />
            )}
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-foreground truncate">
                {attachedFileName || 'Attached Document'}
              </span>
              <span className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">
                  {attachedFileMime === 'application/pdf' ||
                  attachedFileName?.toLowerCase().endsWith('.pdf')
                    ? 'PDF Document'
                    : 'Image'}
                </Badge>
                {attachedFileSize && <span>• {attachedFileSize}</span>}
                <span>• Ready for Gemini extraction</span>
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setAttachedFileBase64(null);
              setAttachedFileName(null);
              setAttachedFileSize(null);
            }}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Remove attachment"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Multimodal Input Footer */}
      <form onSubmit={onFormSubmit} className="p-3 border-t border-border bg-card">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) processAttachedFile(file);
            e.target.value = '';
          }}
        />

        <div className="flex items-center gap-1.5 p-1 rounded-xl border border-input bg-background focus-within:ring-1 focus-within:ring-primary transition-all">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors shrink-0"
            title="Attach PDF work order, compliance certificate, or screenshot"
          >
            <Paperclip className="h-4 w-4" />
          </button>

          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              attachedFileBase64
                ? attachedFileMime === 'application/pdf'
                  ? 'Add instructions for PDF work order...'
                  : 'Add instructions for screenshot...'
                : 'Ask Copilot to ingest PDF, assign, or optimize...'
            }
            className="border-0 shadow-none focus-visible:ring-0 text-xs px-1.5 py-1.5 h-8"
            disabled={isLoading}
          />

          {isLoading ? (
            <button
              type="button"
              onClick={stop}
              className="p-1.5 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors shrink-0"
              title="Stop generation"
            >
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim() && !attachedFileBase64}
              className="p-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              title="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="mt-1.5 px-1 flex items-center justify-between text-[10px] text-muted-foreground">
          <span>Gemini 3.1 Flash Lite • PDFs & Multimodal</span>
          <span>Shift+Enter for newline</span>
        </div>
      </form>
    </aside>
  );
}
